import Vue from 'vue';
import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import moment from 'moment';
import { fetchEnumsFromSchema } from '@/core/tools/forms/fetch_enums_from_schema';

interface ClientFormState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    document: any;
    form: any;
    schema: any;

    meta: any;
}

const state: ClientFormState = {
    loading: false,
    loaded: false,
    fatalError: false,

    document: null,
    form: null,
    schema: null,

    meta: {}
};

const initState = JSON.parse(JSON.stringify(state));

export const CLIENT_DATA = 'GET_CLIENT_DATA';
export const CLIENT_SCHEMA = 'GET_CLIENT_SCHEMA';

export const actions: ActionTree<ClientFormState, RootState> = {
    reset({ commit }) {
        commit('RESET_STATE')
    },

    init({ commit, dispatch }, params) {
        return dispatch('loadData', params);
    },

    loadData({ commit, getters, dispatch }, params) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(CLIENT_DATA)
                    .raw('documents.get')
                    .action({
                        id: params.id
                    }),

            ]).then((result: ServerResult) => {
                commit('SET_FORM_DATA', {
                    document: result.getOperation(CLIENT_DATA).getContent(),
                });

                commit('LOADED_SUCCESS');
                resolve();
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    prepareNew({ commit, state }) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(CLIENT_SCHEMA)
                    .raw('schemas.get')
                    .action({
                        id: `s:clients`
                    }),
            ]).then((result: ServerResult) => {
                commit('GENERATE_FORM_DATA', {
                    schema: result.getOperation(CLIENT_SCHEMA).getContent(),
                });

                commit('LOADED_SUCCESS');
                resolve();
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    toggleCredentials({commit}) {
        commit('TOGGLE_CREDS');
    },

    toggleRoleSelection({commit}, roleID) {
        commit('TOGGLE_ROLE', roleID);
    },

    changeFormValue({commit}, {field, newValue}) {
        commit('CHANGE_VALUE', {field, newValue});
    },
    changeCredentialsValue({commit}, {field, newValue}) {
        commit('CHANGE_CREDS_VALUE', {field, newValue});
    },

    saveForm({ commit, getters, dispatch }, params) {
        const vm = (this as any)._vm;

        const normalizedForm = state.schema.fields.reduce((prev: any, field: any) => {
            const fieldName = field.code;

            if (
                (state.form[fieldName] !== null && state.form[fieldName] !== undefined) || 
                ['head_id', 'parent_id', 'distributor_id', 'agent_id', 'preferred_product_container_id', 'preferred_product_container_2_id',
                'preferred_product_container_3_id', 'secondary_manager_id'].includes(fieldName)
            ) {
                switch(field.type) {
                    case 'date': {
                        prev[fieldName] = moment(state.form[fieldName]).utc().local().format('YYYY-MM-DD');
                        break;
                    }
                    case 'credentials': {
                        prev[fieldName] = {
                            login: state.form[fieldName].login,
                            password: state.form[fieldName].password ? state.form[fieldName].password : ''
                        };
                        break;
                    }
                    default: {
                        prev[fieldName] = state.form[fieldName]
                    }
                }

            }

            return prev
        }, {});

        return new Promise((resolve, reject) => {
            commit('START_LOADING');
            let saveQuery = null;
            if (params.id) {
                saveQuery = vm.$q('CLIENT_DATA_SAVE')
                    .update(params.id, normalizedForm)
                    // .createManyRelation('access_role_grants', 'actor_id', (createRelated: Function) => {
                    //     return state.form.roles.filter((roleId: any) => !state.grants.map((grant: any) => grant.role_id).includes(roleId))
                    //         .map((item: any, index: number) => createRelated({ role_id: item}));
                    // })
                    // .deleteManyRelation('access_role_grants', 'actor_id', (deleteRelated: Function) => {
                    //     return state.grants.filter((grant: any) => !state.form.roles.includes(grant.role_id))
                    //         .map((item: any, index: number) => deleteRelated(item));
                    // })
            } else {
                saveQuery = vm.$q('CLIENT_DATA_SAVE')
                    .create('s:clients', normalizedForm)
                    // .withManyRelation('access_role_grants', 'actor_id', (createRelated: Function) => {
                    //     return state.form.roles.filter((roleId: any) => !state.grants.map((grant: any) => grant.role_id).includes(roleId))
                    //         .map((item: any, index: number) => createRelated({ role_id: item}));
                    // })
            }

            vm.$db([
                saveQuery
            ]).then((result: ServerResult) => {
                const operationResult = result.getOperation('CLIENT_DATA_SAVE');
                if (operationResult.hasError()) {
                    console.log('error', operationResult.getContent());

                    commit('LOADED_SUCCESS');
                    reject();
                } else {
                    commit('ON_FORM_SAVE', { document: operationResult.getContent(), savedGrants: state.form.roles})

                    // create_related
                    //     s:access_role_grants/actor_id
                    //         documents
                    commit('LOADED_SUCCESS');
                    resolve(operationResult.getContent());
                }
            })
            .catch((error: any) => {
                console.log('errror', error)
            });
        })
    },
};

export const mutations: MutationTree<ClientFormState> = {
    RESET_STATE: (state) => {
        for(const key in initState) {
            (state as any)[key] = initState[key]
        }
    },
    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },
    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },
    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_FORM_DATA(state, {document}) {
        state.document = document.document;
        state.schema = document.schema;
        state.meta = fetchEnumsFromSchema(document.schema);

        // grants
        const formData = JSON.parse(JSON.stringify(state.document))
        if(formData.birthday) {
            formData.birthday = moment(formData.birthday).toDate();
        }
        if(formData.works_since) {
            formData.works_since = moment(formData.works_since).toDate();
        }
        if (formData.credentials) {
            formData.credentials = {
                ...formData.credentials,
                password: null,
                repeatPassword: null,
            }
        }

        state.form = formData;
    },

    GENERATE_FORM_DATA(state, {schema}) {
        state.document = {};
        state.schema = schema.schema;
        state.meta = fetchEnumsFromSchema(schema.schema);

        const formData = state.schema.fields.reduce((prev: any, field: any) => {
            prev[field.code] = null;
            return prev;
        }, {})
        formData.roles = [];

        state.form = formData;
    },

    ON_FORM_SAVE(state, {document, savedGrants}) {
        state.document = document.document;
        state.schema = document.schema;
        // state.allRoles = allRoles.documents;
        // state.grants = grants.documents;

        const formData = JSON.parse(JSON.stringify(state.document))
        formData.birthday = moment(formData.birthday).toDate();
        formData.works_since = moment(formData.works_since).toDate();
        if (formData.credentials) {
            formData.credentials = {
                ...formData.credentials,
                password: null,
                repeatPassword: null,
            }
        }
        formData.roles = savedGrants;

        state.form = formData;
    },

    CHANGE_VALUE(state: ClientFormState, {field, newValue}) {
        switch (field) {
            case 'lk_allow_calc_containers': {
                if (!newValue) {
                    state.form['lk_allow_calc_weight'] = true
                    state.form['lk_selected_calc_method'] = 'weight'
                }
                state.form[field] = newValue;
                break
            }
            case 'lk_allow_calc_weight': {
                if (!newValue) {
                    state.form['lk_allow_calc_containers'] = true
                    state.form['lk_selected_calc_method'] = 'containers'
                }
                state.form[field] = newValue;
                break
            }
            case 'credit': {
                state.form[field] = parseInt(newValue);
                break
            }
            default:
                state.form[field] = newValue;
        }
    },
    // TOGGLE_CREDS(state: ClientFormState) {
    //     if (state.form.credentials) {
    //         state.form.credentials = null
    //     } else {
    //         if (state.document.credentials) {
    //             state.form.credentials = {
    //                 ...state.document.credentials,
    //                 password: null,
    //                 repeatPassword: null,
    //             }
    //         } else {
    //             state.form.credentials = {
    //                 login: null,
    //                 password: null,
    //                 repeatPassword: null,
    //             }
    //         }
    //     }
    // },
    // TOGGLE_ROLE(state: ClientFormState, roleId) {
    //     if (state.form.roles.includes(roleId)) {
    //         state.form.roles = state.form.roles.filter((role: any) => role != roleId);
    //     } else {
    //         state.form.roles = [
    //             ...state.form.roles,
    //             roleId
    //         ]
    //     }
    // },
    // CHANGE_CREDS_VALUE(state: ClientFormState, {field, newValue}) {
    //     state.form.credentials[field] = newValue;
    // },
};

const getters = {
    fields: (state: ClientFormState) => {
        return state.schema.fields.reduce((prev: any, field: any) => {
            prev[field.code] = field;
            return prev;
        }, {});
    },
};

export const STORE_KEY = '$_client_details_form';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
